import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Wheat, Cog, FileText, ArrowRight, User } from "lucide-react";
import { Seo } from "../components/Seo";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";

const fadeUp = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] },
};

const PILLARS = [
  { icon: Wheat, title: "Ingredients", desc: "Carefully selected seeds. One ingredient on every label, because only one goes in." },
  { icon: Cog, title: "Process", desc: "Wooden ghani pressing, below 45°C, in small batches. Naturally settled, gently filtered." },
  { icon: FileText, title: "Transparency", desc: "Independent lab testing on every oil — with the reports published for you to read." },
];

export default function AboutPage() {
  return (
    <main data-testid="about-page">
      <Seo title={"About PhudG | Our Story and Philosophy Behind Cold-Pressed Oils"} description={"Learn about PhudG, our approach to traditional cold-pressed oils and our commitment to bringing authentic ingredients to modern Indian kitchens."} image="/images/founder.jpg" path="/about" />
      <Header />
      <section className="bg-ink grain relative pt-36 pb-24 overflow-hidden">
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10">
          <motion.p {...fadeUp} className="text-xs tracking-[0.4em] uppercase text-gold mb-6">
            About PhudG
          </motion.p>
          <motion.h1
            {...fadeUp}
            className="font-serif text-4xl md:text-6xl text-cream tracking-tighter max-w-4xl leading-tight"
          >
            "Why has something as fundamental as cooking oil become so
            difficult to <span className="italic text-gold">understand?</span>"
          </motion.h1>
          <motion.p
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.15 }}
            className="mt-10 max-w-2xl text-base md:text-lg font-light text-cream/65 leading-relaxed"
          >
            We started PhudG with that simple question. Somewhere between the
            seed and the shelf, cooking oil picked up a language of claims,
            asterisks and fine print — and lost the plot.
          </motion.p>
          <motion.p
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.25 }}
            className="mt-5 max-w-2xl text-base md:text-lg font-light text-cream/65 leading-relaxed"
          >
            We wanted to build a food brand around ingredients, process and
            transparency rather than complicated claims. So here's our promise:
            we'll show you our seed, how we press it, what's in the bottle, how
            we test it, and where you can buy it. That's all of it.
          </motion.p>
        </div>
      </section>

      <section data-testid="about-pillars-section" className="bg-cream py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <motion.p {...fadeUp} className="text-xs tracking-[0.4em] uppercase text-amberbrand mb-4">
            What PhudG stands for
          </motion.p>
          <motion.h2 {...fadeUp} className="font-serif text-4xl md:text-6xl text-ink tracking-tighter mb-14">
            Three words we <span className="italic text-olive">work by.</span>
          </motion.h2>
          <div className="grid sm:grid-cols-3 gap-6">
            {PILLARS.map((p, i) => {
              const Icon = p.icon;
              return (
                <motion.div
                  key={p.title}
                  data-testid={`about-pillar-${i}`}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.7, delay: i * 0.1 }}
                  className="rounded-3xl border border-ink/10 bg-white/70 p-8"
                >
                  <span className="mb-6 flex h-12 w-12 items-center justify-center rounded-full bg-olive/10 text-olive">
                    <Icon size={20} />
                  </span>
                  <h3 className="font-serif text-2xl text-ink mb-3">{p.title}</h3>
                  <p className="text-sm font-light text-ink/55 leading-relaxed">{p.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      <section data-testid="founder-note-section" className="bg-cream pb-24 md:pb-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid md:grid-cols-12 gap-12 items-center">
            <motion.div {...fadeUp} className="md:col-span-4">
              <div className="overflow-hidden rounded-t-[999px] rounded-b-3xl border border-ink/10 max-w-xs mx-auto">
                <img
                  src="/images/founder.jpg"
                  alt="Amandeep, Founder of PhudG"
                  data-testid="founder-photo"
                  loading="lazy"
                  className="w-full aspect-[4/5] object-cover"
                />
              </div>
            </motion.div>
            <div className="md:col-span-8">
              <motion.p {...fadeUp} className="text-xs tracking-[0.4em] uppercase text-amberbrand mb-6">
                A note from our founder
              </motion.p>
              {[
                "Before starting PhudG, I thought I knew what went into my family's food.",
                "When I started reading the labels on the cooking oils in my own kitchen, I couldn't pronounce half of what was in them — refined, bleached, chemically extracted, sitting on a shelf for months. I didn't want that on my family's table, and I didn't think anyone else should have to settle for it either.",
                "So I built PhudG. We press our oils the slow way, in a traditional wooden kolhu, below 45°C — no refining, no chemicals, no preservatives, nothing added. Just seed to oil, the way it was done before the shortcuts.",
                "Every bottle is something I use in my own home, without a second thought about my family's health. That's the only standard I hold this brand to: if I wouldn't cook with it, I won't sell it.",
              ].map((para, i) => (
                <motion.p
                  key={i}
                  {...fadeUp}
                  transition={{ ...fadeUp.transition, delay: 0.1 + i * 0.08 }}
                  data-testid={`founder-note-para-${i}`}
                  className={
                    i === 0
                      ? "font-serif text-2xl md:text-3xl text-ink leading-snug mb-6"
                      : "text-base font-light text-ink/65 leading-relaxed mb-5 max-w-2xl"
                  }
                >
                  {para}
                </motion.p>
              ))}
              <motion.p
                {...fadeUp}
                transition={{ ...fadeUp.transition, delay: 0.45 }}
                data-testid="founder-signature"
                className="mt-8 font-serif text-2xl italic text-olive"
              >
                — Amandeep, Founder
              </motion.p>
            </div>
          </div>
          <motion.div {...fadeUp} className="mt-14 text-center">
            <Link
              to="/contact"
              data-testid="about-contact-link"
              className="inline-flex items-center gap-2 text-sm tracking-[0.2em] uppercase text-amberbrand hover:text-ink transition-colors duration-300"
            >
              Get in touch <ArrowRight size={16} />
            </Link>
          </motion.div>
        </div>
      </section>
      <Footer />
    </main>
  );
}
